/**
 * Defaults for operator/government create panels from dashboard filters.
 *
 * Only applies when exactly one jurisdiction and/or license type is selected.
 * Does not invent values when filters are empty or multi-select.
 */

export type CreateFormQuestionScope = "global" | "group" | "jurisdiction";

export type CreateFormFilterDefaultsInput = {
  selectedJurisdictions?: string[] | null;
  selectedLicenseTypes?: string[] | null;
  /**
   * Scope when filters do not force jurisdiction mode (e.g. government always
   * jurisdiction, or a parent already chose a scope).
   */
  fallbackScope?: CreateFormQuestionScope | null;
  /** When true, always use jurisdiction scope (government create). */
  forceJurisdictionScope?: boolean;
  /** Government user's jurisdiction; used when forceJurisdictionScope is set. */
  governmentJurisdictionId?: string | null;
};

export type CreateFormFilterDefaults = {
  scope: CreateFormQuestionScope;
  /** Jurisdiction to pre-select, or null when not implied by filters. */
  jurisdictionId: string | null;
  /**
   * License type to pre-select, or null when not implied by filters
   * (caller may fall back to first available license type).
   */
  licenseTypeId: string | null;
};

function singleId(ids: string[] | null | undefined): string | null {
  if (!ids || ids.length !== 1) {
    return null;
  }
  const id = ids[0];
  return id ? id : null;
}

/**
 * Resolve create-form scope / jurisdiction / license type from dashboard filters.
 *
 * - Exactly one jurisdiction → jurisdiction-scoped + that jurisdiction
 * - Exactly one license type → that license type; prefer global (license-type-wide)
 *   when jurisdiction is not forced
 * - Both singles → jurisdiction scope with both pre-selected
 */
export function getCreateFormFilterDefaults(
  input: CreateFormFilterDefaultsInput = {},
): CreateFormFilterDefaults {
  const singleJurisdiction = singleId(input.selectedJurisdictions);
  const singleLicenseType = singleId(input.selectedLicenseTypes);
  const fallbackScope = input.fallbackScope ?? "global";

  if (input.forceJurisdictionScope) {
    return {
      scope: "jurisdiction",
      jurisdictionId:
        input.governmentJurisdictionId ?? singleJurisdiction ?? null,
      licenseTypeId: singleLicenseType,
    };
  }

  if (singleJurisdiction) {
    return {
      scope: "jurisdiction",
      jurisdictionId: singleJurisdiction,
      licenseTypeId: singleLicenseType,
    };
  }

  // Single license type only: stay on license-type-wide (global) mode.
  if (singleLicenseType) {
    return {
      scope: fallbackScope === "jurisdiction" ? "global" : fallbackScope,
      jurisdictionId: null,
      licenseTypeId: singleLicenseType,
    };
  }

  return {
    scope: fallbackScope,
    jurisdictionId: null,
    licenseTypeId: null,
  };
}
